import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Description,
  Label,
  Modal,
  NumberField,
  Separator,
  toast,
} from '@heroui/react';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { trpc } from '@/lib/trpc';
import wepayIconUrl from '@/assets/icons/wepay.svg';
import { WepayModal, type WepayModalCheckout } from './WepayModal';

/** 与 sku.billing_period_unit 枚举 label 对齐，见 sku.table.ts BILLING_PERIOD_UNIT_OPTIONS */
const BILLING_PERIOD_UNIT_LABELS: Record<string, string> = {
  MONTH: '月',
  YEAR: '年',
  NONE: '无周期',
};

type BillingPeriodUnit = 'MONTH' | 'YEAR' | 'NONE';

export type PlanCheckoutProduct = {
  id: string;
  name?: string | null;
  code?: string | null;
  type?: string | null;
  uom?: string | null;
  billingPeriodUnit?: string | null;
  prices?: Array<{
    id?: string;
    billingMode?: string;
    unitPrice?: number | string;
  }>;
};

interface PlanCheckoutModalProps {
  open: boolean;
  product: PlanCheckoutProduct | null;
  billingPeriodUnit: BillingPeriodUnit;
  initialAccounts?: number;
  onCancel: () => void;
  onPaid?: () => void;
}

function formatUnitPrice(unitPrice: number | string | undefined): number {
  if (unitPrice === undefined || unitPrice === null) return 0;
  const n = typeof unitPrice === 'string' ? Number(unitPrice) : unitPrice;
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value: number): string {
  return value.toFixed(2);
}

function isPlanProduct(type: string | null | undefined): boolean {
  return type === 'PLAN';
}

function resolveQuantityLimit(
  type: string | null | undefined,
  code: string | null | undefined
): {
  minValue: number;
  locked: boolean;
} {
  if (!isPlanProduct(type)) {
    return { minValue: 1, locked: false };
  }
  const productCode = code ?? '';
  if (productCode.includes('PERSONAL')) {
    return { minValue: 1, locked: true };
  }
  if (productCode.includes('TEAM')) {
    return { minValue: 3, locked: false };
  }
  if (productCode.includes('ENTERPRISE')) {
    return { minValue: 5, locked: false };
  }
  return { minValue: 1, locked: false };
}

function clampAccountCount(value: number, minValue: number, locked: boolean): number {
  if (locked) return 1;
  if (!Number.isFinite(value) || value < minValue) return minValue;
  return Math.floor(value);
}

function billingPeriodLabel(unit: string | null | undefined): string {
  if (!unit) return '—';
  return BILLING_PERIOD_UNIT_LABELS[unit] ?? unit;
}

export function PlanCheckoutModal({
  open,
  product,
  billingPeriodUnit,
  initialAccounts = 1,
  onCancel,
  onPaid,
}: PlanCheckoutModalProps) {
  const [periodCount, setPeriodCount] = useState(1);
  const isPlan = isPlanProduct(product?.type);
  const heading = isPlan ? '订阅计划' : '权益扩容';
  const productNameLabel = isPlan ? '计划名称' : '产品名称';
  const quantityLabel = isPlan ? '空间人数' : '购买数量';
  const quantityUom = product?.uom?.trim() || (isPlan ? '人' : '件');

  const [accountCount, setAccountCount] = useState(() => {
    const limit = resolveQuantityLimit(product?.type, product?.code);
    return clampAccountCount(initialAccounts, limit.minValue, limit.locked);
  });
  const [paying, setPaying] = useState(false);
  const [wepayOpen, setWepayOpen] = useState(false);
  const [checkout, setCheckout] = useState<WepayModalCheckout | null>(null);

  const createCheckout = trpc.bizOrder.createWechatNative.useMutation();

  const seatLimit = useMemo(
    () => resolveQuantityLimit(product?.type, product?.code),
    [product?.type, product?.code]
  );

  useEffect(() => {
    if (!open) return;
    setPeriodCount(1);
    setAccountCount(clampAccountCount(initialAccounts, seatLimit.minValue, seatLimit.locked));
    setPaying(false);
  }, [open, initialAccounts, product?.id, seatLimit.minValue, seatLimit.locked]);

  const selectedPrice = useMemo(() => {
    return (
      product?.prices?.find((p) => p.billingMode === billingPeriodUnit) ??
      product?.prices?.[0] ??
      null
    );
  }, [product?.prices, billingPeriodUnit]);

  const unitPrice = formatUnitPrice(selectedPrice?.unitPrice);
  const total = unitPrice * periodCount * accountCount;
  const periodUnitLabel = billingPeriodLabel(billingPeriodUnit);

  const handleOpenChange = (next: boolean) => {
    if (!next) onCancel();
  };

  const handlePay = async () => {
    if (!product?.id) {
      toast.danger(isPlan ? '未选择套餐' : '未选择商品');
      return;
    }
    if (!selectedPrice?.id) {
      toast.danger('当前计费周期暂无有效价格');
      return;
    }
    if (total <= 0) {
      toast.danger('支付金额必须大于 0');
      return;
    }

    const quantity = clampAccountCount(accountCount, seatLimit.minValue, seatLimit.locked);

    setPaying(true);
    try {
      const result = await createCheckout.mutateAsync({
        skuId: selectedPrice.id,
        periodQuantity: periodCount,
        seatQuantity: quantity,
      });
      if (!result.paymentId || !result.codeUrl) {
        toast.danger('支付下单成功但缺少支付参数');
        return;
      }
      setCheckout({
        paymentId: result.paymentId,
        codeUrl: result.codeUrl,
        amount: result.amountYuan,
        productName: result.skuName,
      });
      setWepayOpen(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : '创建支付失败';
      toast.danger(message);
    } finally {
      setPaying(false);
    }
  };

  const handleWepayPaid = () => {
    setWepayOpen(false);
    setCheckout(null);
    onPaid?.();
    onCancel();
  };

  const handleWepayCancel = () => {
    setWepayOpen(false);
  };

  return (
    <>
      <Modal isOpen={open && !wepayOpen} onOpenChange={handleOpenChange}>
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" size="md">
            <Modal.Dialog>
              <Modal.CloseTrigger />
              <Modal.Header>
                <Modal.Heading className="text-xl font-bold">{heading}</Modal.Heading>
              </Modal.Header>

              <Modal.Body className="gap-0 p-0">
                <div className="flex items-center justify-between gap-4 py-4">
                  <Label className="text-sm text-default-600">{productNameLabel}</Label>
                  <span className="text-sm font-medium text-foreground">
                    {product?.name ?? '—'}
                  </span>
                </div>
                <Separator />

                <div className="flex items-center justify-between gap-4 py-4">
                  <Label className="shrink-0 text-sm text-default-600">计费周期</Label>
                  <span className="text-sm font-medium text-foreground">{periodUnitLabel}</span>
                </div>
                <Separator />

                <div className="flex items-center justify-between gap-4 py-4">
                  <Label className="shrink-0 text-sm text-default-600">订阅时间</Label>
                  <div className="flex items-center gap-2">
                    <NumberField
                      aria-label="订阅时间"
                      minValue={1}
                      value={periodCount}
                      variant="secondary"
                      onChange={(value) =>
                        setPeriodCount(Number.isFinite(value) && value >= 1 ? value : 1)
                      }
                    >
                      <NumberField.Group>
                        <NumberField.DecrementButton />
                        <NumberField.Input className="w-16 text-center" />
                        <NumberField.IncrementButton />
                      </NumberField.Group>
                    </NumberField>
                    <span className="text-sm text-default-600">{periodUnitLabel}</span>
                  </div>
                </div>
                <Separator />

                <div className="flex items-center justify-between gap-4 py-4">
                  <Label className="shrink-0 text-sm text-default-600">{quantityLabel}</Label>
                  <div className="flex items-center gap-2">
                    <NumberField
                      aria-label={quantityLabel}
                      minValue={seatLimit.minValue}
                      maxValue={seatLimit.locked ? 1 : undefined}
                      isDisabled={seatLimit.locked}
                      value={accountCount}
                      variant="secondary"
                      onChange={(value) =>
                        setAccountCount(
                          clampAccountCount(value, seatLimit.minValue, seatLimit.locked)
                        )
                      }
                    >
                      <NumberField.Group>
                        <NumberField.DecrementButton />
                        <NumberField.Input className="w-16 text-center" />
                        <NumberField.IncrementButton />
                      </NumberField.Group>
                    </NumberField>
                    <span className="text-sm text-default-600">{quantityUom}</span>
                  </div>
                </div>
                <Separator />

                <div className="flex items-center justify-between gap-4 py-4">
                  <Label className="text-sm text-default-600">单价</Label>
                  <span className="text-sm text-foreground">
                    {formatMoney(unitPrice)}
                    <span className="ml-1 text-default-600">元</span>
                  </span>
                </div>
                <Separator />

                <div className="flex items-center justify-between gap-4 py-4">
                  <Label className="text-sm text-default-600">合计</Label>
                  <span className="text-sm font-medium text-foreground">
                    {formatMoney(total)}
                    <span className="ml-1 font-normal text-default-600">元</span>
                  </span>
                </div>

                <div className="mb-4 mt-2 rounded-lg border border-default-200 p-4">
                  <p className="mb-3 text-sm font-medium text-foreground">支付方式</p>
                  <div className="flex items-center gap-3 rounded-md bg-default-50 py-2.5">
                    <img src={wepayIconUrl} alt="微信支付" className="size-6 shrink-0" />
                    <span className="text-sm text-foreground">微信支付</span>
                  </div>
                  <Description className="mt-2 text-xs text-default-500">
                    当前仅支持微信支付
                  </Description>
                </div>
              </Modal.Body>

              <Modal.Footer>
                <Button type="button" variant="ghost" onPress={onCancel} isDisabled={paying}>
                  取消
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  isDisabled={paying || !selectedPrice?.id}
                  isPending={paying}
                  onPress={() => void handlePay()}
                >
                  立即支付
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      <WepayModal
        open={wepayOpen}
        checkout={checkout}
        onCancel={handleWepayCancel}
        onPaid={handleWepayPaid}
      />
    </>
  );
}
